<template>
	<div :class="containerClass">
		<h3 v-if="title" class="text-lg font-semibold mb-4">
			{{ title }}
		</h3>

		<div class="flex justify-center space-x-4">
			<div v-for="(unit, key) in timeUnits" :key="key" class="text-center">
				<div :class="numberClass">
					{{ unit.value }}
				</div>
				<div :class="labelClass">
					{{ unit.label }}
				</div>
			</div>
		</div>

		<p v-if="expiredText && isExpired" class="text-center mt-4 font-medium">
			{{ expiredText }}
		</p>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useComponentTracking } from '@/composables/useComponentTracking';

interface Props {
	endDate: Date;
	title?: string;
	expiredText?: string;
	variant?: 'default' | 'urgent' | 'minimal';
	size?: 'sm' | 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
	title: 'Angebot endet in:',
	expiredText: 'Angebot abgelaufen',
	variant: 'default',
	size: 'md',
});

// Initialize tracking
const { trackComponentView, trackEngagement } = useComponentTracking({
	componentName: 'MarketingCountdown',
	sectionId: 'countdown',
	sectionType: 'urgency',
});

const timeLeft = ref({
	days: 0,
	hours: 0,
	minutes: 0,
	seconds: 0,
});

const isExpired = ref(false);
let timer: number | null = null;

const timeUnits = computed(() => ({
	days: {
		value: String(timeLeft.value.days).padStart(2, '0'),
		label: timeLeft.value.days === 1 ? 'Tag' : 'Tage',
	},
	hours: {
		value: String(timeLeft.value.hours).padStart(2, '0'),
		label: timeLeft.value.hours === 1 ? 'Stunde' : 'Stunden',
	},
	minutes: {
		value: String(timeLeft.value.minutes).padStart(2, '0'),
		label: timeLeft.value.minutes === 1 ? 'Minute' : 'Minuten',
	},
	seconds: {
		value: String(timeLeft.value.seconds).padStart(2, '0'),
		label: timeLeft.value.seconds === 1 ? 'Sekunde' : 'Sekunden',
	},
}));

const containerClass = computed(() => {
	const variants = {
		default: 'bg-white/10 rounded-lg p-6',
		urgent: 'bg-red-100 border-2 border-red-300 rounded-lg p-6',
		minimal: 'p-4',
	};

	return variants[props.variant];
});

const numberClass = computed(() => {
	const sizes = {
		sm: 'text-2xl',
		md: 'text-3xl',
		lg: 'text-4xl',
	};

	const variants = {
		default: 'font-bold text-white',
		urgent: 'font-bold text-red-600',
		minimal: 'font-bold text-gray-900',
	};

	return `${sizes[props.size]} ${
		variants[props.variant]
	} bg-black/20 rounded-lg px-3 py-2 min-w-[60px]`;
});

const labelClass = computed(() => {
	const variants = {
		default: 'text-white/80',
		urgent: 'text-red-600',
		minimal: 'text-gray-600',
	};

	return `text-sm font-medium mt-1 ${variants[props.variant]}`;
});

function updateCountdown() {
	const now = new Date().getTime();
	const endTime = props.endDate.getTime();
	const difference = endTime - now;

	if (difference <= 0) {
		isExpired.value = true;
		timeLeft.value = { days: 0, hours: 0, minutes: 0, seconds: 0 };
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
		return;
	}

	timeLeft.value = {
		days: Math.floor(difference / (1000 * 60 * 60 * 24)),
		hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
		minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
		seconds: Math.floor((difference % (1000 * 60)) / 1000),
	};
}

onMounted(() => {
	trackComponentView();
	updateCountdown();
	timer = window.setInterval(updateCountdown, 1000);

	// Track when countdown expires
	if (isExpired.value) {
		trackEngagement('countdown_expired', {
			variant: props.variant,
		});
	}
});

onUnmounted(() => {
	if (timer) {
		clearInterval(timer);
	}
});
</script>
